import { Injectable, computed, inject, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'

import { filter, first, lastValueFrom } from 'rxjs'

import { FlxThemeApiService } from './theme-api.service'
import { Property, Theme } from './theme-model'
import { ColorTokens } from './theme-model/colors'
import * as ThemeHelpers from './theme-model/theme.helpers'
import { TypographyTokens } from './theme-model/typography'

import { ApiTheme } from '../../cms/pages/themes/theme.dictionary'
import { FlxThemesManagementService } from '../../cms/pages/themes/themes-management.service'
import { Platform } from '../model/platform.model'
import { Section } from '../model/sections.model'

const emptyCompiledTheme = (): Record<Platform, any> => ({
  [Platform.Android]: null,
  [Platform.Ios]: null,
  [Platform.Web]: null,
})

const DEFAULT_PLATFORM = Platform.Web

@Injectable()
export class FlxThemesLoaderService {
  readonly #themeManagement = inject(FlxThemesManagementService)
  readonly #themeApi = inject(FlxThemeApiService)

  readonly themeList = signal<ApiTheme[]>([])
  readonly defaultTheme = computed(
    () =>
      this.themeList().find((theme) => theme.defaultTheme) ||
      this.themeList().find((theme) => theme.systemTheme)
  )
  readonly selectedTheme = signal<ApiTheme>(null)

  readonly currentTheme = signal<Theme>(null)
  readonly currentPlatform = signal<Platform>(DEFAULT_PLATFORM)
  readonly compiledCurrentTheme = signal<Record<Platform, any>>(emptyCompiledTheme()) // to type this as theme

  readonly compiledCurrentThemeForCurrentPlatform = computed(() => {
    const platform = this.currentPlatform()
    const compiledTheme = this.compiledCurrentTheme()
    if (platform && compiledTheme) {
      return compiledTheme[platform]
    }
    return null
  })

  readonly colorGroupOptions = computed(() =>
    ThemeHelpers.getColorOptionGroups(
      this.#getThemeDataAndPlatformForPath<ColorTokens>(Section.Color)
    )
  )

  readonly typographyGroupOptions = computed(() =>
    ThemeHelpers.getTypographyOptionGroups(
      this.#getThemeDataAndPlatformForPath<TypographyTokens>(Section.Typography)
    )
  )

  readonly shadowOptions = computed(() =>
    ThemeHelpers.getShadowOptions(this.#getThemeDataAndPlatformForPath(Section.Shadows))
  )

  // temp: this is used by the component styling services, will change when we move to signals all across the app
  readonly compiledThemes$ = toObservable(this.compiledCurrentThemeForCurrentPlatform).pipe(
    filter((compiled) => !!compiled)
  )

  async init(): Promise<void> {
    this.themeList.set(await lastValueFrom(this.#themeManagement.allThemes$.pipe(first())))
  }

  async selectTheme(flowxUuid: ApiTheme['flowxUuid']): Promise<void> {
    this.selectedTheme.set(this.themeList().find((theme) => theme.flowxUuid === flowxUuid))
    this.currentTheme.set(
      await lastValueFrom(this.#themeApi.getTheme(this.selectedTheme().flowxUuid))
    )
    this.compiledCurrentTheme.set(emptyCompiledTheme())
    await this.updateComputedTheme()
  }

  async changePlatform(platform: Platform): Promise<void> {
    this.currentPlatform.set(platform)
    if (this.currentTheme()) {
      await this.updateComputedTheme()
    }
  }

  async updateComputedTheme(): Promise<void> {
    const compiledCurrentTheme = this.compiledCurrentTheme()
    if (compiledCurrentTheme[this.currentPlatform()]) {
      return
    }
    // cache the compiled theme for the current platform
    this.compiledCurrentTheme.set({
      ...compiledCurrentTheme,
      [this.currentPlatform()]: await lastValueFrom(
        this.#themeApi.getCompiledTheme(this.selectedTheme().flowxUuid, this.currentPlatform())
      ),
    })
  }

  async resetPlatform(): Promise<void> {
    await this.changePlatform(DEFAULT_PLATFORM)
  }

  getThemePropertyAtPathAndPlatform(path: string, platform: Platform): any {
    const propertyPath = ThemeHelpers.getThemePathForPlatform(path, platform)
    const property = ThemeHelpers.getThemeDataFromPath<Property>(this.currentTheme(), propertyPath)
    const calculatedProperty = ThemeHelpers.getPropertyWithCalculatedValues(
      this.currentTheme(),
      platform,
      property
    )
    return ThemeHelpers.getPropertyValue(calculatedProperty)
  }

  #getThemeDataAndPlatformForPath<T>(path: string): T {
    return ThemeHelpers.getThemeDataFromPath<T>(
      this.currentTheme(),
      ThemeHelpers.getThemePathForPlatform(path, this.currentPlatform())
    )
  }
}
